import { Box } from '@mui/material';
import { getAllSigners } from 'lib/api/business';
import { Signer, SignersResponse } from 'lib/api/business/types';
import GlobalSearcher from 'lib/common/GlobalSearcher';
import { Filter } from 'lib/common/GlobalSearcher/filterTypes';
import {
  translateCountry,
  translateMaritalStatus,
} from 'lib/consts/usersFields';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { Pagination } from 'lib/types/shared';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import UpdateSigner from './UpdateSigner';

const BASE_PAGINATION: Pagination = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

const tableHeaders = [
  { key: 'identifier', label: 'Rut' },
  { key: 'name', label: 'Nombre' },
  { key: 'email', label: 'Email', type: 'copyable' },
  { key: 'phone', label: 'Teléfono' },
  { key: 'translateCountry', label: 'Nacionalidad' },
  { key: 'profession', label: 'Profesión' },
  { key: 'translateMaritalStatus', label: 'Estado civil' },
  { key: 'company', label: 'Empresa', type: 'dual-text' },
];

const Signers = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<SignersResponse['data']>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [identifier, setIdentifier] = useState<string>('');
  const [openUpdateSigner, setOpenUpdateSigner] = useState(false);
  const [selectedSigner, setSelectedSigner] = useState<Signer | null>(null);

  const filters: Filter[] = [
    {
      type: 'autocomplete',
      label: 'Empresa',
      key: 'businessIdentifier',
      value: identifier,
      setter: setIdentifier,
    },
  ];

  const generateQuery = ({
    page,
    pageSize,
    identifier,
  }: {
    page: number;
    pageSize: number;
    identifier: string;
  }) => {
    const urlParams = new URLSearchParams();
    if (identifier) urlParams.set('identifier', identifier);
    if (page) urlParams.set('page', page.toString());
    if (pageSize) urlParams.set('pageSize', pageSize.toString());
    return urlParams.toString();
  };

  const fetchSigners = async (params?: { page: number; pageSize: number }) => {
    try {
      setLoading(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const query = generateQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
        identifier,
      });
      const response = await getAllSigners(dispatch, query);
      const signers =
        response?.data?.map((signer) => ({
          ...signer,
          translateCountry: translateCountry(signer.country),
          translateMaritalStatus: translateMaritalStatus(signer.maritalStatus),
        })) ?? [];
      setData(signers);
      setPagination(response?.pagination ?? BASE_PAGINATION);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickRow = (rowId: number) => {
    const signer = data.find((s) => s.id === rowId);
    if (signer) {
      setSelectedSigner(signer);
      setOpenUpdateSigner(true);
    }
  };

  useEffect(() => {
    void fetchSigners({ page: 1, pageSize: 10 });
  }, []);

  return (
    <Box mt={2} position="relative">
      <Box sx={{ position: 'absolute', top: -52, right: 0 }}>
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => fetchSigners()}
          dispatch={dispatch}
        />
      </Box>
      <TableWithPagination
        data={data}
        setData={setData}
        dataHeaders={tableHeaders}
        pagination={pagination}
        setPagination={setPagination}
        loading={loading}
        searchData={fetchSigners}
        handleClickRow={handleClickRow}
      />
      <UpdateSigner
        open={openUpdateSigner}
        setOpen={setOpenUpdateSigner}
        fetchSigners={() => fetchSigners()}
        signer={selectedSigner}
      />
    </Box>
  );
};

export default Signers;
